import { message } from 'antd';
import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import {
  connectOmniChannelSocket,
  disconnectOmniChannelSocket,
  onReceiveCallCdrEvent,
  onReceiveCallDialAnswerEvent,
  onReceiveCallDialingEvent,
  onReceiveCallHangUpEvent,
  useLazyGetAgentStatusQuery,
  useToggleRecordMutation
} from 'services';
import { AgentStatusDto } from 'types';
import { AGENT_STATUS, ChannelObject } from 'utils';
import { useAddQueueMember, useAgentStatusChange, useGetShiftMeActive, useProfile, useRemoveQueueMember } from 'hooks';
import { useCreateContactEventMutation } from 'services/tickets';
import { useToggleScreenRecord } from 'hooks/screen-record';
import { CustomerSupportSession } from 'pages/CustomerSupport';
import { flushSync } from 'react-dom';

// Enhanced ContextProps to include all agent status management functions
export type OmniCallContextProps = {
  // Original context values
  socketData: any;
  agentStatus: any;
  setGetAgentStatus: any;
  getAgentStatus: any;

  // Agent status functions moved from useAgentStatusManager
  updateAgentStatusReady: () => void;
  updateAgentStatusPause: (reasonId?: number) => void;
  updateAgentStatusCreatingTicket: () => void;
  updateAgentStatusFailedLogin: () => void;
  updateAgentStatusOnCall: () => void;
  updateAgentStatusMuteCall: () => void;
  updateAgentStatusRinging: () => void;
  toggleAgentStatus: (
    action: AGENT_STATUS.AGENT_PAUSED | AGENT_STATUS.AGENT_UNPAUSED,
    reasonId?: number
  ) => Promise<void>;
  toggleAgentStatusWithoutChangingLayout: (
    action: AGENT_STATUS.AGENT_PAUSED | AGENT_STATUS.AGENT_UNPAUSED
  ) => Promise<void>;

  // Current states
  currentState: string;
  isTogglingStatus: boolean;
  isRemovingFromQueue: boolean;

  // Refs
  isStatusTransitioning: React.RefObject<boolean>;

  // Add missed call state and handlers
  showMissedCallModal: boolean;
  missedCallData: {
    customerNumber?: string;
    callSource?: string;
    callTime?: string;
  };
  handleMissedCallModalClose: () => void;
};

export const OmniCallContext = createContext<OmniCallContextProps | undefined>(undefined);

export const OmniCallProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // State variables
  const { profile } = useProfile();
  const { shiftMeActive } = useGetShiftMeActive();
  const [socketData, setSocketData] = useState<any>();
  const [showPopUpReceiveCall, setShowPopUpReceiveCall] = useState(false);
  const [customerNumber, setCustomerNumber] = useState('');
  const [fetchAgentStatus, { data: agentStatusRes, error, isLoading }] = useLazyGetAgentStatusQuery();
  const [getAgentStatus, setGetAgentStatus] = useState<{ worldPhoneState?: AGENT_STATUS; agentState?: AGENT_STATUS }>(
    {}
  );

  // State for agent status management
  const [currentState, setCurrentState] = useState<string>(AGENT_STATUS.UNKNOWN);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [isRemovingFromQueue, setIsRemovingFromQueue] = useState(false);

  // Refs
  const createdContactEventsRef = useRef<Set<string>>(new Set());
  const contactEventTimestampRef = useRef<Map<string, number>>(new Map());
  const isStatusTransitioning = useRef(false);
  const lastQueueOpTime = useRef<number>(0);
  const lastAgentStatusFetch = useRef<number>(0);
  const prevAgentStatusRes = useRef<any>(null);
  const lastScreenRecordTime = useRef<number>(0); // Add new ref to track screen recording calls
  const processedCallsRef = useRef<Set<string>>(new Set()); // Track processed calls to avoid duplicate incomingCallReceived events

  // Add refs to track call state
  const wasRingingRef = useRef(false);
  const currentCallDataRef = useRef<any>(null);
  const pendingRingingCallDataRef = useRef<any>(null); // Ref to store deferred ringing call data

  // Hooks
  const { onCreateAgentStatusChangeHandle } = useAgentStatusChange();
  const { onRemoveQueueMember } = useRemoveQueueMember();
  const { onAddQueueMember } = useAddQueueMember();
  const [onCreateContactEvent] = useCreateContactEventMutation();
  const { onToggleScreenRecordHandle } = useToggleScreenRecord();
  const sourceDisplayNames: Record<string, string> = {
    telco: 'VNPT',
    webcall: 'WEBCALL',
    appcall: 'APPCALL',
    voice: 'VOICE',
    video: 'VIDEO'
  };

  // Map callSource to channelId
  const sourceToChannelMapping: Record<string, number> = {
    telco: ChannelObject.DIEN_THOAI.id,
    webcall: ChannelObject.DIEN_THOAI_WEB.id,
    appcall: ChannelObject.DIEN_THOAI_APP.id,
    voice: ChannelObject.DIEN_THOAI.id,
    video: ChannelObject.WEB360_VIDEO.id
  };
  useEffect(() => {
    connectOmniChannelSocket();

    // ✅ RESTORE: Restore currentCallDataRef from localStorage after F5
    try {
      const storedCallEvent = localStorage.getItem('currentCallDialingEvent');
      if (storedCallEvent) {
        const callData = JSON.parse(storedCallEvent);
        currentCallDataRef.current = callData;
        wasRingingRef.current = true; // Assume it was ringing before F5
        console.log('🔄 Restored currentCallDataRef after F5:', callData);
      }
    } catch (error) {
      console.error('❌ Error restoring currentCallDataRef:', error);
    }

    return () => {
      // disconnectOmniChannelSocket();
      createdContactEventsRef.current.clear();
      contactEventTimestampRef.current.clear();
      // disconnectOmniChannelSocket();
    };
  }, []);

  const getSourceDisplayName = (source: string): string => {
    return sourceDisplayNames[source.toLowerCase()] || source.toUpperCase();
  };

  // Get channelId from callSource
  const getChannelIdFromSource = (source: string): number => {
    return sourceToChannelMapping[source?.toLowerCase()]; // Default to DIEN_THOAI if mapping not found
  };
  // Functions
  const handleFetchStatus = () => {
    if (profile?.extensionId) {
      fetchAgentStatus({ extension: profile.extensionId });
    }
  };

  const safeHandleFetchStatus = () => {
    if (profile?.extensionId) {
      const now = Date.now();
      const timeSinceLastCall = now - lastAgentStatusFetch.current;

      if (timeSinceLastCall < 5000) {
        // 5 second protection

        return;
      }

      lastAgentStatusFetch.current = now;
      fetchAgentStatus({ extension: profile.extensionId });
    }
  };

  // Extract agent status from API response
  const extractAgentStatus = useCallback((agentStatusArr: AgentStatusDto[]) => {
    if (!agentStatusArr.length) return null;

    const statusDto = agentStatusArr[0];
    const worldPhoneState = statusDto.state as AGENT_STATUS;
    const queue = statusDto.queues?.queue || [];
    const isAgentCreatingTicket = localStorage.getItem('isAgentCreatingTicket');

    if (isAgentCreatingTicket) {
      return {
        worldPhoneState,
        agentState: AGENT_STATUS.CREATING_TICKET
      };
    } else {
      const agentState =
        statusDto.state === AGENT_STATUS.IDLE
          ? queue.length > 0
            ? AGENT_STATUS.AGENT_UNPAUSED
            : AGENT_STATUS.AGENT_PAUSED
          : (statusDto.state as AGENT_STATUS);

      return {
        worldPhoneState,
        agentState
      };
    }
  }, []);

  // Status management functions
  const toggleAgentStatusWithoutChangingLayout = useCallback(
    async (action: AGENT_STATUS.AGENT_PAUSED | AGENT_STATUS.AGENT_UNPAUSED) => {
      try {
        if (!profile?.extensionId) {
          message.error('Extension ID không hợp lệ');
          console.error('Invalid profile extension ID');
          return Promise.reject('Invalid extension ID');
        }

        // Debounce protection: prevent duplicate calls within 2 seconds
        const now = Date.now();
        if (isRemovingFromQueue || now - lastQueueOpTime.current < 2000) {
          return Promise.resolve(); // Return success to allow state transitions to continue
        }

        setIsRemovingFromQueue(true);
        lastQueueOpTime.current = now;

        const actionFunction = action === AGENT_STATUS.AGENT_PAUSED ? onRemoveQueueMember : onAddQueueMember;

        const queueResult = await actionFunction({ extension: profile.extensionId });
        const aqmStatuses = queueResult?.data?.map((item) => item.aqmStatus);

        if (action === AGENT_STATUS.AGENT_PAUSED) {
          if (aqmStatuses.includes('REMOVED') || aqmStatuses.includes('NOSUCHQUEUE')) {
            message.success('Chuyển đổi trạng thái: Đang tạo phiếu');
            return Promise.resolve();
          } else {
            return Promise.reject('Failed to remove agent from queues');
          }
        }

        return Promise.resolve();
      } catch (error) {
        console.error(`Gặp lỗi khi đổi trạng thái ${action}:`, error);
        message.error(`Gặp lỗi khi đổi trạng thái ${action}`);
        return Promise.reject(error);
      } finally {
        // Always reset the flag after operation completes
        setTimeout(() => {
          setIsRemovingFromQueue(false);
        }, 1000); // Add a small delay to prevent rapid consecutive calls
      }
    },
    [profile, onRemoveQueueMember, onAddQueueMember, isRemovingFromQueue]
  );

  const updateAgentStatusReady = useCallback(() => {
    //console.log('🔄 Attempting to set agent status to READY');

    // Only take special precautions if transitioning from CREATING_TICKET
    if (currentState === AGENT_STATUS.CREATING_TICKET) {
      isStatusTransitioning.current = true;
      //console.log('OCP_DEBUG: isStatusTransitioning set to true (entering READY from CREATING_TICKET).');

      try {
        setGetAgentStatus({ worldPhoneState: AGENT_STATUS.IDLE, agentState: AGENT_STATUS.AGENT_UNPAUSED });
        setCurrentState(AGENT_STATUS.AGENT_UNPAUSED);
        window.dispatchEvent(
          new CustomEvent('formFullReset', {
            detail: {
              timestamp: Date.now(),
              forceComplete: true
            }
          })
        );
        if (profile?.userId && profile?.extensionId) {
          onCreateAgentStatusChangeHandle({
            userId: profile?.userId,
            extensionId: profile?.extensionId.toString(),
            previousState: currentState,
            nextState: AGENT_STATUS.AGENT_UNPAUSED
          });
        }

        localStorage.removeItem('isAgentCreatingTicket');
        // localStorage.setItem("isUpdateAgentStatusReady", "true") // REMOVE
        // localStorage.setItem("isUpdateAgentStatusReady", "false") // REMOVE

        // Clear call event and remove from customerSupportSession
        const raw = localStorage.getItem('currentCallDialingEvent');
        if (raw) {
          const { parentCallUuid, callUuid } = JSON.parse(raw);
          const callId = parentCallUuid || callUuid;

          // XÓA HẲN localStorage thay vì chỉ đánh dấu completed
          localStorage.removeItem('currentCallDialingEvent');

          // Remove the session from customerSupportSession
          const callIdToRemove =
            currentCallDataRef?.current?.parentCallUuid || parentCallUuid || currentCallDataRef?.current?.callUuid;
          if (callIdToRemove) {
            try {
              const storedSessions = localStorage.getItem('customerSupportSession');
              if (storedSessions) {
                let sessions = JSON.parse(storedSessions) as CustomerSupportSession[];
                const initialLength = sessions.length;
                sessions = sessions.filter((session) => session.callId !== callIdToRemove);
                if (sessions.length < initialLength) {
                  localStorage.setItem('customerSupportSession', JSON.stringify(sessions));
                  localStorage.removeItem('currentCallDialingEvent'); // Clear the dialing event as well
                  //console.log(`📞 Missed call: Removed session for callId ${callIdToRemove} from customerSupportSession.`);
                }
              }
            } catch (error) {
              console.error('Error removing missed call session from localStorage:', error);
            }
          }

          // Ensure a complete UI reset by dispatching an event
          window.dispatchEvent(
            new CustomEvent('agentTicketingCompleted', {
              detail: {
                callId,
                timestamp: Date.now()
              }
            })
          );
        }
      } finally {
        // Always make sure we clear the transitioning flag
        setTimeout(() => {
          flushSync(() => {
            isStatusTransitioning.current = false;
            //console.log('OCP_DEBUG: isStatusTransitioning set to false (READY transition completed).');
          });
          flushSync(() => {
            // Extra flushSync to ensure UI fully updates in rare cases
            //console.log('OCP_DEBUG: Second flushSync for rare concurrency issues.');
          });
          if (pendingRingingCallDataRef.current) {
            //console.log('OCP_DEBUG: Processing deferred RINGING state after READY transition completed. CallData:', pendingRingingCallDataRef.current);
            // currentCallDataRef should have already been set by handleDialingEvent
            updateAgentStatusRinging();
            pendingRingingCallDataRef.current = null; // Clear pending data
          }
        }, 500); // Short delay to ensure state updates complete
      }
    } else {
      // Regular update for non-critical transitions
      setGetAgentStatus({ worldPhoneState: AGENT_STATUS.IDLE, agentState: AGENT_STATUS.AGENT_UNPAUSED });

      // Only create status change if we have a valid previous state and it's different
      if (profile?.userId && profile?.extensionId && currentState && currentState !== AGENT_STATUS.AGENT_UNPAUSED) {
        onCreateAgentStatusChangeHandle({
          userId: profile?.userId,
          extensionId: profile?.extensionId.toString(),
          previousState: currentState,
          nextState: AGENT_STATUS.AGENT_UNPAUSED
        });
      }

      setCurrentState(AGENT_STATUS.AGENT_UNPAUSED);
      localStorage.removeItem('isAgentCreatingTicket');
    }
  }, [currentState, profile, onCreateAgentStatusChangeHandle]);

  const updateAgentStatusPause = useCallback(
    (reasonId?: number) => {
      //console.log('vào hàm sau login nè')
      setGetAgentStatus({
        worldPhoneState: AGENT_STATUS.IDLE,
        agentState: AGENT_STATUS.AGENT_PAUSED
      });

      // Only create status change if we have a valid previous state and it's different
      if (profile?.userId && profile?.extensionId && currentState && currentState !== AGENT_STATUS.AGENT_PAUSED) {
        onCreateAgentStatusChangeHandle({
          userId: profile?.userId,
          extensionId: profile?.extensionId.toString(),
          previousState: currentState,
          nextState: AGENT_STATUS.AGENT_PAUSED,
          reasonId: reasonId || 0
        });
      }

      setCurrentState(AGENT_STATUS.AGENT_PAUSED);
    },
    [currentState, profile, onCreateAgentStatusChangeHandle]
  );

  const updateAgentStatusCreatingTicket = useCallback(() => {
    // First, call the API to remove agent from queues (PAUSE)
    toggleAgentStatusWithoutChangingLayout(AGENT_STATUS.AGENT_PAUSED)
      .then(() => {
        // Only after API success, update the frontend state
        setCurrentState(AGENT_STATUS.CREATING_TICKET);
        setGetAgentStatus({
          worldPhoneState: AGENT_STATUS.IDLE,
          agentState: AGENT_STATUS.CREATING_TICKET
        });

        // Always set the localStorage flag when entering CREATING_TICKET state
        localStorage.setItem('isAgentCreatingTicket', '1');
      })
      .catch((error) => {
        console.error('Failed to pause agent before setting CREATING_TICKET state:', error);
        message.error('Không thể chuyển sang trạng thái tạo phiếu');
      });
  }, [toggleAgentStatusWithoutChangingLayout]);

  const updateAgentStatusFailedLogin = useCallback(() => {
    setGetAgentStatus({
      worldPhoneState: AGENT_STATUS.LOGGEDOFF,
      agentState: AGENT_STATUS.FAILED_LOGIN
    });

    if (profile?.userId && profile?.extensionId && currentState && currentState !== AGENT_STATUS.FAILED_LOGIN) {
      onCreateAgentStatusChangeHandle({
        userId: profile?.userId,
        extensionId: profile?.extensionId.toString(),
        previousState: currentState,
        nextState: AGENT_STATUS.FAILED_LOGIN
      });
    }

    setCurrentState(AGENT_STATUS.FAILED_LOGIN);
  }, [currentState, profile, onCreateAgentStatusChangeHandle]);

  // Add debounce ref for updateAgentStatusOnCall
  const onCallDebounceRef = useRef<boolean>(false);

  const updateAgentStatusOnCall = useCallback(() => {
    // 🚨 PREVENT MULTIPLE CALLS: Add debounce to prevent spam
    if (onCallDebounceRef.current) {
      //console.log('🙅 PREVENTED: updateAgentStatusOnCall already executing, skipping duplicate call');
      return;
    }

    onCallDebounceRef.current = true;
    setTimeout(() => {
      onCallDebounceRef.current = false;
    }, 1000); // Reset after 1 second

    //console.log('vào hàm update oncall')
    setCurrentState(AGENT_STATUS.ONCALL);
    setGetAgentStatus({
      worldPhoneState: AGENT_STATUS.ONCALL,
      agentState: AGENT_STATUS.ONCALL
    });
  }, []);

  const updateAgentStatusMuteCall = useCallback(() => {
    setGetAgentStatus({
      worldPhoneState: AGENT_STATUS.ONCALL,
      agentState: AGENT_STATUS.MUTE_ON
    });

    if (profile?.userId && profile?.extensionId && currentState && currentState !== AGENT_STATUS.MUTE_ON) {
      onCreateAgentStatusChangeHandle({
        userId: profile?.userId,
        extensionId: profile?.extensionId.toString(),
        previousState: currentState,
        nextState: AGENT_STATUS.MUTE_ON
      });
    }

    setCurrentState(AGENT_STATUS.MUTE_ON);
  }, [currentState, profile, onCreateAgentStatusChangeHandle]);

  const updateAgentStatusRinging = useCallback(() => {
    //console.log('🔔 Setting agent to RINGING state');

    // Đặt trạng thái RINGING
    setCurrentState(AGENT_STATUS.RINGING);
    setGetAgentStatus({
      worldPhoneState: AGENT_STATUS.RINGING,
      agentState: AGENT_STATUS.RINGING
    });
  }, []);

  const toggleAgentStatus = useCallback(
    async (action: AGENT_STATUS.AGENT_PAUSED | AGENT_STATUS.AGENT_UNPAUSED, reasonId?: number) => {
      // If already toggling status or operation was too recent, skip
      const now = Date.now();
      if (isTogglingStatus || isRemovingFromQueue || now - lastQueueOpTime.current < 2000) {
        return;
      }
      if (action === AGENT_STATUS.AGENT_UNPAUSED) {
        updateAgentStatusReady();
        window.dispatchEvent(
          new CustomEvent('formFullReset', {
            detail: {
              timestamp: Date.now(),
              forceComplete: true
            }
          })
        );
      }
      setIsTogglingStatus(true);
      lastQueueOpTime.current = now;
      try {
        if (!profile?.extensionId) {
          message.error('Extension ID không hợp lệ');
          console.error('Invalid profile extension ID');
          return;
        }

        // Xác định action phù hợp
        const actionFunction = action === AGENT_STATUS.AGENT_PAUSED ? onRemoveQueueMember : onAddQueueMember;
        // if (AGENT_STATUS.AGENT_UNPAUSED) { // REMOVE THIS BLOCK
        //   localStorage.setItem("isUpdateAgentStatusReady", "true")
        // }
        // Gọi API để cập nhật trạng thái
        const queueResult = await actionFunction({ extension: profile.extensionId });

        // Lấy danh sách trạng thái từ phản hồi
        const aqmStatuses = queueResult?.data?.map((item) => item.aqmStatus);

        // Kiểm tra trạng thái để quyết định chuyển đổi
        if (action === AGENT_STATUS.AGENT_PAUSED) {
          if (aqmStatuses.includes('REMOVED') || aqmStatuses.includes('NOSUCHQUEUE')) {
            message.success('Chuyển đổi trạng thái: Bận');
            // Pass reasonId to updateAgentStatusPause
            //console.log()
            updateAgentStatusPause(reasonId);
          } else {
            message.error('Chuyển đổi trạng thái không thành công');
          }
        } else {
          if (aqmStatuses.includes('ADDED') || aqmStatuses.includes('MEMBERALREADY')) {
            //updateAgentStatusReady();
            message.success('Chuyển đổi trạng thái: Sẵn sàng');
          } else {
            message.error('Chuyển đổi trạng thái không thành công');
          }
        }
      } catch (error) {
        console.error(`Gặp lỗi khi đổi trạng thái ${action}:`, error);
        message.error(`Gặp lỗi khi đổi trạng thái ${action}`);
      } finally {
        setIsTogglingStatus(false); // Set loading to false
      }
    },
    [
      profile,
      onRemoveQueueMember,
      onAddQueueMember,
      isTogglingStatus,
      isRemovingFromQueue,
      updateAgentStatusPause,
      updateAgentStatusReady
    ]
  );

  // State variables
  const [showMissedCallModal, setShowMissedCallModal] = useState(false);
  const [missedCallData, setMissedCallData] = useState<{
    customerNumber?: string;
    callSource?: string;
    callTime?: string;
  }>({
    customerNumber: '',
    callSource: '',
    callTime: ''
  });

  // Functions
  const handleMissedCallModalClose = useCallback(() => {
    setShowMissedCallModal(false);
    setMissedCallData({
      customerNumber: '',
      callSource: '',
      callTime: ''
    });
    wasRingingRef.current = false;
    currentCallDataRef.current = null;
  }, []);

  // Effects
  useEffect(() => {
    safeHandleFetchStatus();
  }, [profile?.extensionId]);

  // Socket setup
  useEffect(() => {
    // Connect only once on mount

    const handleDialingEvent = (data: any) => {
      window.dispatchEvent(
        new CustomEvent('formFullReset', {
          detail: {
            timestamp: Date.now(),
            forceComplete: true
          }
        })
      );
      // --- clear previous call data to avoid showing old number ---
      flushSync(() => {
        setSocketData(null);
      });
      currentCallDataRef.current = null;

      if (data?.callUuid && data?.customerNumber) {
        // Instead of setting setSocketData(data) immediately, use setTimeout to ensure UI clears first
        setTimeout(() => {
          setSocketData(data);
          currentCallDataRef.current = data; // Always update current call data

          // ✅ ADD: Ensure call session exists in localStorage immediately
          const callId = data.parentCallUuid || data.callUuid;
          try {
            const sessions = JSON.parse(localStorage.getItem('customerSupportSession') || '[]');
            if (!sessions.some((s: any) => s.callId === callId)) {
              sessions.push({
                callId,
                searchData: [],
                type: 'call'
              });
              localStorage.setItem('customerSupportSession', JSON.stringify(sessions));
            }
          } catch (error) {
            console.error('Error ensuring call session exists:', error);
          }

          // Track that this call was ringing for missed call detection
          wasRingingRef.current = true;

          // ✅ MODIFIED: Logic for handling RINGING during transitions.
          // The isUpdatingAgentStatus (localStorage check) block is removed.
          // The existing isStatusTransitioning.current check should cover critical transitions like CREATING_TICKET -> READY.
          // For other cases, the 'else' block will set RINGING immediately,
          // and updateAgentStatusReady() has its own protection (forcedRingingStateRef.current)
          // to prevent overriding an active RINGING state.
          if (isStatusTransitioning.current) {
            // This primarily handles CREATING_TICKET -> READY

            pendingRingingCallDataRef.current = data; // For after transition completes
            updateAgentStatusRinging(); // Set RINGING immediately
          }
          // Nếu không có vấn đề gì, hiển thị RINGING ngay lập tức
          else {
            updateAgentStatusRinging();
            pendingRingingCallDataRef.current = null; // Clear any stale pending data
          }

          // Dispatch a custom event to notify the system about the incoming call with enhanced detail
          if (!processedCallsRef.current.has(callId)) {
            processedCallsRef.current.add(callId);

            // Dispatch a custom event to notify the system about the incoming call with enhanced detail
            window.dispatchEvent(
              new CustomEvent('incomingCallReceived', {
                detail: {
                  callId: data.parentCallUuid || data.callUuid,
                  customerNumber: data.customerNumber,
                  callData: data,
                  timestamp: Date.now(),
                  forceReset: true, // Flag to force component resets
                  cleanForm: true // Explicit flag for form clearing
                }
              })
            );
            //console.log('📞 handleDialingEvent: Dispatched incomingCallReceived for callId:', callId);
          } else {
            //console.log('🚫 handleDialingEvent: Skipped duplicate incomingCallReceived to avoid interrupting user for callId:', callId);
          }

          if (profile?.userId && profile?.username && shiftMeActive?.shiftId && data?.parentCallUuid) {
            const callId = data.parentCallUuid;

            const now = Date.now();
            const lastCalled = contactEventTimestampRef.current.get(callId) || 0;

            // Nếu đã xử lý rồi => bỏ qua
            if (createdContactEventsRef.current.has(callId)) {
              return;
            }

            // Nếu gọi quá nhanh (dưới 2 giây) => bỏ qua
            if (now - lastCalled < 2000) {
              return;
            }

            // Đánh dấu thời gian gọi gần nhất
            contactEventTimestampRef.current.set(callId, now);

            onCreateContactEvent({
              agentId: profile.userId,
              agentName: profile.username,
              channelId: getChannelIdFromSource(data?.callSource),
              contactAccount: data.customerNumber,
              contactId: callId,
              flowCode: data.direction,
              shiftId: shiftMeActive.shiftId,
              callId: callId
            })
              .unwrap()
              .then((response) => {
                createdContactEventsRef.current.add(callId);
                saveContactResponseToSession(callId, response);
                localStorage.setItem('currentCallDialingEvent', JSON.stringify(data));
              })
              .catch((error) => {
                console.error('❌ Failed to create contact event:', error);
              });
          }
        }, 0);
      }
    };

    const handleDialAnswerEvent = (data: any) => {
      if (data?.callUuid && data?.customerNumber) {
        // Instead of setting setSocketData(data) immediately, use setTimeout to ensure UI clears first
        setTimeout(() => {
          const callId = data.parentCallUuid || data.callUuid;
          if (!processedCallsRef.current.has(callId)) {
            processedCallsRef.current.add(callId);
            window.dispatchEvent(
              new CustomEvent('formFullReset', {
                detail: {
                  timestamp: Date.now(),
                  forceComplete: true
                }
              })
            );
            // --- clear previous call data to avoid showing old number ---
            flushSync(() => {
              setSocketData(null);
            });
            currentCallDataRef.current = null;
            setSocketData(data);
            currentCallDataRef.current = data; // Always update current call data

            // ✅ ADD: Ensure call session exists in localStorage immediately

            try {
              const sessions = JSON.parse(localStorage.getItem('customerSupportSession') || '[]');
              if (!sessions.some((s: any) => s.callId === callId)) {
                sessions.push({
                  callId,
                  searchData: [],
                  type: 'call'
                });
                localStorage.setItem('customerSupportSession', JSON.stringify(sessions));
              }
            } catch (error) {
              console.error('Error ensuring call session exists:', error);
            }

            // Track that this call was ringing for missed call detection
            wasRingingRef.current = true;

            // ✅ MODIFIED: Logic for handling RINGING during transitions.
            // The isUpdatingAgentStatus (localStorage check) block is removed.
            // The existing isStatusTransitioning.current check should cover critical transitions like CREATING_TICKET -> READY.
            // For other cases, the 'else' block will set RINGING immediately,
            // and updateAgentStatusReady() has its own protection (forcedRingingStateRef.current)
            // to prevent overriding an active RINGING state.
            if (isStatusTransitioning.current) {
              // This primarily handles CREATING_TICKET -> READY

              pendingRingingCallDataRef.current = data; // For after transition completes
              updateAgentStatusOnCall(); // Set RINGING immediately
            }
            // Nếu không có vấn đề gì, hiển thị RINGING ngay lập tức
            else {
              updateAgentStatusOnCall();
              pendingRingingCallDataRef.current = null; // Clear any stale pending data
            }

            // Dispatch a custom event to notify the system about the incoming call with enhanced detail

            // Dispatch a custom event to notify the system about the incoming call with enhanced detail
            window.dispatchEvent(
              new CustomEvent('incomingCallReceived', {
                detail: {
                  callId: data.parentCallUuid || data.callUuid,
                  customerNumber: data.customerNumber,
                  callData: data,
                  timestamp: Date.now(),
                  forceReset: true, // Flag to force component resets
                  cleanForm: true // Explicit flag for form clearing
                }
              })
            );
            //console.log('📞 handleDialAnswerEvent: Dispatched incomingCallReceived for callId:', callId);

            // 🚨 FIX: Add debounce protection for screen recording to prevent multiple calls
            const now = Date.now();
            const timeSinceLastScreenRecord = now - lastScreenRecordTime.current;

            if (timeSinceLastScreenRecord >= 2000) {
              // 2 second protection
              lastScreenRecordTime.current = now;

              onToggleScreenRecordHandle({
                isRec: true,
                fileName: `${data.callUuid || Date.now()}.mp4`, // Use data.callUuid instead of socketData
                id: data.callUuid || 'call'
              })
                .then((response) => {
                  console.log('✅ Screen recording started successfully for call:', data.callUuid);
                })
                .catch((error) => {
                  console.error('❌ Failed to start screen recording:', error);
                });
            } else {
              console.log('🚫 Screen recording call skipped - too recent (debounced)');
            }

            console.log(profile?.userId, profile?.username, shiftMeActive?.shiftId, data?.parentCallUuid);
            if (profile?.userId && profile?.username && shiftMeActive?.shiftId && data?.parentCallUuid) {
              const callId = data.parentCallUuid;

              const now = Date.now();
              const lastCalled = contactEventTimestampRef.current.get(callId) || 0;

              // Nếu đã xử lý rồi => bỏ qua
              if (createdContactEventsRef.current.has(callId)) {
                return;
              }

              // Nếu gọi quá nhanh (dưới 2 giây) => bỏ qua
              if (now - lastCalled < 2000) {
                return;
              }

              // Đánh dấu thời gian gọi gần nhất
              contactEventTimestampRef.current.set(callId, now);
              //console.log('create contact event')
              onCreateContactEvent({
                agentId: profile.userId,
                agentName: profile.username,
                channelId: getChannelIdFromSource(data?.callSource),
                contactAccount: data.customerNumber,
                contactId: callId,
                flowCode: data.direction || 'inbound',
                shiftId: shiftMeActive.shiftId
              })
                .unwrap()
                .then((response) => {
                  createdContactEventsRef.current.add(callId);
                  saveContactResponseToSession(callId, response);
                  localStorage.setItem('currentCallDialingEvent', JSON.stringify(data));
                })
                .catch((error) => {
                  console.error('❌ Failed to create contact event:', error);
                });
            }
          } else {
            //console.log('🚫 handleDialAnswerEvent: Skipped duplicate incomingCallReceived to avoid interrupting user for callId:', callId);
            wasRingingRef.current = true;

            // ✅ MODIFIED: Logic for handling RINGING during transitions.
            // The isUpdatingAgentStatus (localStorage check) block is removed.
            // The existing isStatusTransitioning.current check should cover critical transitions like CREATING_TICKET -> READY.
            // For other cases, the 'else' block will set RINGING immediately,
            // and updateAgentStatusReady() has its own protection (forcedRingingStateRef.current)
            // to prevent overriding an active RINGING state.
            if (isStatusTransitioning.current) {
              // This primarily handles CREATING_TICKET -> READY

              pendingRingingCallDataRef.current = data; // For after transition completes
              updateAgentStatusOnCall(); // Set RINGING immediately
            }
            // Nếu không có vấn đề gì, hiển thị RINGING ngay lập tức
            else {
              updateAgentStatusOnCall();
              pendingRingingCallDataRef.current = null; // Clear any stale pending data
            }
          }
        }, 0);
      }
    };

    // Helper function to save contact response to session
    const saveContactResponseToSession = (callId: string, contactResponse: any) => {
      try {
        const dataField = contactResponse.data;

        const storedSessions = localStorage.getItem('customerSupportSession');
        if (!storedSessions) {
          // If no sessions exist yet, create a new one with contactResponse
          const newSession = {
            callId,
            searchData: [],
            type: 'call',
            contactResponse // Store the contact response
          };
          localStorage.setItem('customerSupportSession', JSON.stringify([newSession]));

          // ✅ ADD: Dispatch event to notify system that a session was created
          window.dispatchEvent(
            new CustomEvent('callSessionCreated', {
              detail: { callId, timestamp: Date.now() }
            })
          );

          return;
        }

        const sessions = JSON.parse(storedSessions);
        // Find the session for this call
        const sessionIndex = sessions.findIndex((s: any) => s.callId === callId);

        if (sessionIndex >= 0) {
          // Update existing session with contact response
          sessions[sessionIndex].contactResponse = dataField;
        } else {
          // Create new session with contactResponse
          sessions.push({
            callId,
            searchData: [],
            type: 'call',
            contactResponse: dataField
          });

          // ✅ ADD: Dispatch event to notify system that a session was created
          window.dispatchEvent(
            new CustomEvent('callSessionCreated', {
              detail: { callId, timestamp: Date.now() }
            })
          );
        }

        // Save back to localStorage
        localStorage.setItem('customerSupportSession', JSON.stringify(sessions));
      } catch (error) {
        console.error('❌ Error saving contact response to session:', error);
      }
    };

    // const handleHangUpEvent = (data: any) => {
    //   setSocketData(data);

    //   // Set appropriate states
    //   updateAgentStatusCreatingTicket()
    // };

    const handleCdrEvent = (data: any) => {
      //setSocketData(data);
      //console.log('event cdr nè', data)
      // Kiểm tra xem sự kiện CDR này có phải là cho cuộc gọi hiện tại không
      const currentCallId = currentCallDataRef?.current?.parentCallUuid || currentCallDataRef?.current?.callUuid;

      // Kiểm tra xem có phải là missed call không
      const isMissedCall =
        wasRingingRef.current &&
        currentCallDataRef?.current &&
        (data.disposition === 'NO ANSWER' || data.disposition === 'CANCEL' || data.disposition === 'BUSY');

      // Chỉ xử lý nếu callUuid khớp với cuộc gọi hiện tại hoặc là missed call
      if (currentCallId && data.callUuid === currentCallId) {
        if (data.disposition === 'ANSWERED') {
          updateAgentStatusCreatingTicket();
        } else if (isMissedCall) {
          // Xử lý missed call cho cuộc gọi hiện tại
          const missedCallData = {
            ...currentCallDataRef.current,
            customerNumber: data?.customerNumber || currentCallDataRef?.current?.customerNumber,
            callSource: data?.callSource || currentCallDataRef?.current?.callSource
          };
          handleMissedCall(missedCallData, true);
        }
      } else if (isMissedCall && currentCallDataRef?.current) {
        // Xử lý missed call cho các cuộc gọi khác
        const missedCallData = {
          ...currentCallDataRef.current,
          customerNumber: data?.customerNumber || currentCallDataRef?.current?.customerNumber,
          callSource: data?.callSource || currentCallDataRef?.current?.callSource
        };
        handleMissedCall(missedCallData, false);
      }
    };

    const handleMissedCall = (callData: any, isCurrentCall: boolean) => {
      if (!callData) return;

      // Show missed call modal with call data
      setMissedCallData({
        customerNumber: callData.customerNumber,
        callSource: getSourceDisplayName(callData.callSource || ''),
        callTime: new Date().toLocaleTimeString('vi-VN', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      });
      setShowMissedCallModal(true);

      // ✅ ADD: Remove missed call session from customerSupportSession
      const callIdToRemove = callData.parentCallUuid || callData.callUuid;
      if (callIdToRemove) {
        try {
          const storedSessions = localStorage.getItem('customerSupportSession');
          if (storedSessions) {
            let sessions = JSON.parse(storedSessions) as CustomerSupportSession[];
            const initialLength = sessions.length;
            sessions = sessions.filter((session) => session.callId !== callIdToRemove);
            if (sessions.length < initialLength) {
              localStorage.setItem('customerSupportSession', JSON.stringify(sessions));
              localStorage.removeItem('currentCallDialingEvent'); // Clear the dialing event as well
              //console.log(`📞 Missed call: Removed session for callId ${callIdToRemove} from customerSupportSession.`);
            }
          }
        } catch (error) {
          console.error('Error removing missed call session from localStorage:', error);
        }
      }

      // Also notify other components about the missed call
      window.dispatchEvent(
        new CustomEvent('callMissed', {
          detail: {
            callId: callIdToRemove,
            customerNumber: callData?.customerNumber,
            timestamp: Date.now()
          }
        })
      );

      // Chỉ cập nhật trạng thái và xóa dữ liệu nếu đây là cuộc gọi hiện tại
      if (isCurrentCall) {
        updateAgentStatusPause();
        wasRingingRef.current = false;
        currentCallDataRef.current = null;
      }
    };

    // Attach event handlers
    onReceiveCallDialingEvent(handleDialingEvent);
    onReceiveCallDialAnswerEvent(handleDialAnswerEvent);
    onReceiveCallCdrEvent(handleCdrEvent);
  }, [
    shiftMeActive,
    getSourceDisplayName,
    updateAgentStatusRinging,
    updateAgentStatusCreatingTicket,
    updateAgentStatusPause
  ]);

  // Update agent status from API response with protection
  useEffect(() => {
    if (!agentStatusRes || prevAgentStatusRes.current === agentStatusRes) {
      return;
    }

    prevAgentStatusRes.current = agentStatusRes;

    const newStatus = extractAgentStatus(agentStatusRes.data);

    if (newStatus) {
      // Update current state tracking when we get fresh data from API
      if (newStatus.agentState) {
        setCurrentState(newStatus.agentState);
      }

      // Only update if there's an actual change to avoid loops
      setGetAgentStatus((prev) => {
        if (prev.agentState === newStatus.agentState && prev.worldPhoneState === newStatus.worldPhoneState) {
          return prev; // No change, return existing state
        }

        return newStatus; // Only update on actual changes
      });
    }
  }, [agentStatusRes, extractAgentStatus, currentState]);

  return (
    <OmniCallContext.Provider
      value={{
        // Original values
        socketData,
        agentStatus: getAgentStatus?.agentState,
        setGetAgentStatus,
        getAgentStatus,

        // Agent status management (moved from useAgentStatusManager)
        updateAgentStatusReady,
        updateAgentStatusPause,
        updateAgentStatusCreatingTicket,
        updateAgentStatusFailedLogin,
        updateAgentStatusOnCall,
        updateAgentStatusMuteCall,
        updateAgentStatusRinging,
        toggleAgentStatus,
        toggleAgentStatusWithoutChangingLayout,
        currentState,
        isTogglingStatus,
        isRemovingFromQueue,
        isStatusTransitioning,

        // Add missed call functionality
        showMissedCallModal,
        missedCallData,
        handleMissedCallModalClose
      }}
    >
      {children}
    </OmniCallContext.Provider>
  );
};

export default OmniCallProvider;
